import React, { useState, useEffect, useRef } from 'react';
import firebase from 'firebase/compat/app';
import { TimeEntry } from '../types';
import { AddIcon, PlayIcon, PauseIcon, StopIcon, ReloadIcon, EditIcon, DeleteIcon } from './icons';
import ConfirmationModal from './ConfirmationModal';
import ReauthDeleteModal from './ReauthDeleteModal';


interface TimeLogViewProps {
    timeEntries: TimeEntry[];
    categories: string[];
    onAddTimeEntry: (entryData: Omit<TimeEntry, 'id'>) => void;
    onUpdateTimeEntry: (id: string, entryData: Omit<TimeEntry, 'id'>) => void;
    onDeleteTimeEntry: (id: string) => void;
    user: firebase.User | null;
    onBulkUpdate: (ids: string[], data: Partial<Omit<TimeEntry, 'id'>>) => void;
    onBulkDelete: (ids: string[]) => void;
}

const formatTime = (seconds: number): string => {
    const h = Math.floor(seconds / 3600).toString().padStart(2, '0');
    const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
    const s = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
};

const parseTimeToHours = (time: string | number): number => {
    if (typeof time === 'number') {
        return time;
    }
    if (typeof time === 'string') {
        if (!time.includes(':')) {
            const parsed = parseFloat(time);
            return isNaN(parsed) ? 0 : parsed;
        }
        const parts = time.split(':').map(Number);
        const [h = 0, m = 0, s = 0] = parts;
        if (isNaN(h) || isNaN(m) || isNaN(s)) {
            return 0;
        }
        return h + m / 60 + s / 3600;
    }
    return 0;
};


const TimerDisplay: React.FC<{
    seconds: number;
    maxSeconds?: number;
    label: string;
    progressColor: string;
}> = ({ seconds, maxSeconds = 3600, label, progressColor }) => {
    const radius = 94;
    const strokeWidth = 10;
    const innerRadius = radius - strokeWidth / 2;
    const circumference = 2 * Math.PI * innerRadius;
    const progress = maxSeconds > 0 ? Math.min(seconds / maxSeconds, 1) : 0;
    const offset = circumference - progress * circumference;

    return (
        <div className="relative w-60 h-60 flex flex-col items-center justify-center">
            <svg className="absolute top-0 left-0 w-full h-full transform -rotate-90" viewBox="0 0 200 200">
                <circle
                    className="text-gray-200 dark:text-gray-600"
                    strokeWidth={strokeWidth}
                    stroke="currentColor"
                    fill="transparent"
                    r={innerRadius}
                    cx="100"
                    cy="100"
                />
                <circle
                    className={progressColor}
                    strokeWidth={strokeWidth}
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    stroke="currentColor"
                    fill="transparent"
                    r={innerRadius}
                    cx="100"
                    cy="100"
                    style={{ transition: 'stroke-dashoffset 0.3s linear' }}
                />
            </svg>
            <div className="z-10 text-center">
                <span className="text-4xl font-mono text-light-text-primary dark:text-text-primary">{formatTime(seconds)}</span>
                <span className="block text-sm text-light-text-secondary dark:text-text-secondary mt-1">{label}</span>
            </div>
        </div>
    );
};


const TimeLogView: React.FC<TimeLogViewProps> = ({ timeEntries, categories, onAddTimeEntry, onUpdateTimeEntry, onDeleteTimeEntry, user, onBulkUpdate, onBulkDelete }) => {
    const [newEntry, setNewEntry] = useState({ date: new Date().toISOString().split('T')[0], category: categories[0] || '', task: '', hours: '0' });
    const [editingEntryId, setEditingEntryId] = useState<string | null>(null);

    // Timer & Stopwatch state
    const [stopwatchSeconds, setStopwatchSeconds] = useState(0);
    const [isStopwatchRunning, setIsStopwatchRunning] = useState(false);
    const stopwatchIntervalRef = useRef<number | null>(null);

    const [initialTimerSeconds, setInitialTimerSeconds] = useState(25 * 60);
    const [timerSeconds, setTimerSeconds] = useState(25 * 60);
    const [timerInputMinutes, setTimerInputMinutes] = useState('25');
    const [isTimerRunning, setIsTimerRunning] = useState(false);
    const timerIntervalRef = useRef<number | null>(null);

    // State for bulk actions and modals
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [deletingEntry, setDeletingEntry] = useState<TimeEntry | null>(null);
    const [modalState, setModalState] = useState<{ type: 'category' | 'delete' | null; isOpen: boolean }>({ type: null, isOpen: false });
    const [bulkCategory, setBulkCategory] = useState('');

    // Stopwatch logic
    useEffect(() => {
        if (isStopwatchRunning) {
            stopwatchIntervalRef.current = window.setInterval(() => {
                setStopwatchSeconds(prev => prev + 1);
            }, 1000);
        } else if (stopwatchIntervalRef.current) {
            clearInterval(stopwatchIntervalRef.current);
        }
        return () => { if (stopwatchIntervalRef.current) clearInterval(stopwatchIntervalRef.current) };
    }, [isStopwatchRunning]);

    // Timer logic
    useEffect(() => {
        if (isTimerRunning && timerSeconds > 0) {
            timerIntervalRef.current = window.setInterval(() => {
                setTimerSeconds(prev => prev - 1);
            }, 1000);
        } else if (timerSeconds === 0 && isTimerRunning) {
            alert("¡Tiempo finalizado!");
            transferTime(initialTimerSeconds);
            setIsTimerRunning(false);
            setTimerSeconds(initialTimerSeconds); // Reset after completion
            if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
        } else {
            if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
        }
        return () => { if (timerIntervalRef.current) clearInterval(timerIntervalRef.current) };
    }, [isTimerRunning, timerSeconds, initialTimerSeconds]);

    const handleSetTimer = () => {
        const minutes = parseInt(timerInputMinutes, 10);
        if (!isNaN(minutes) && minutes > 0) {
            const newSeconds = minutes * 60;
            setIsTimerRunning(false);
            setTimerSeconds(newSeconds);
            setInitialTimerSeconds(newSeconds);
        }
    };
    
    const transferTime = (seconds: number) => {
        if (seconds <= 0) return;
        const timeString = formatTime(seconds);
        setNewEntry(prev => ({...prev, hours: timeString }));
    };

    const handleStopwatchStopAndTransfer = () => {
        setIsStopwatchRunning(false);
        transferTime(stopwatchSeconds);
        setStopwatchSeconds(0);
    };
    
    const handleTimerStopAndTransfer = () => {
        setIsTimerRunning(false);
        const elapsedSeconds = initialTimerSeconds - timerSeconds;
        transferTime(elapsedSeconds);
        setTimerSeconds(initialTimerSeconds);
    };

    const handleTimerReset = () => {
        setIsTimerRunning(false);
        setTimerSeconds(initialTimerSeconds);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setNewEntry(prev => ({ ...prev, [name]: value }));
    };

    const resetForm = () => {
        setNewEntry({ date: new Date().toISOString().split('T')[0], category: categories[0] || '', task: '', hours: '0' });
        setEditingEntryId(null);
    };

    const handleSaveEntry = (e: React.FormEvent) => {
        e.preventDefault();
        const hoursAsNumber = parseTimeToHours(newEntry.hours);

        if (newEntry.task && hoursAsNumber > 0 && newEntry.category) {
            const entryData = { ...newEntry, hours: hoursAsNumber };
            if (editingEntryId) {
                onUpdateTimeEntry(editingEntryId, entryData);
            } else {
                onAddTimeEntry(entryData);
            }
            resetForm();
        } else {
            alert('Por favor, seleccione una categoría, complete la tarea y las horas (deben ser mayores que cero).');
        }
    };
    
    const handleEditEntry = (entry: TimeEntry) => {
        setEditingEntryId(entry.id);
        setNewEntry({
            date: entry.date,
            category: entry.category,
            task: entry.task,
            hours: formatTime(entry.hours * 3600)
        });
        window.scrollTo(0, 0);
    };

    const handleSelect = (id: string) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setSelectedIds(timeEntries.map(t => t.id));
        } else {
            setSelectedIds([]);
        }
    };
    
    const handleConfirmBulkAction = async () => {
        if (modalState.type === 'category' && bulkCategory) {
            await onBulkUpdate(selectedIds, { category: bulkCategory });
        }
        setModalState({ type: null, isOpen: false });
        setSelectedIds([]);
        setBulkCategory('');
    };

    const handleConfirmBulkDelete = async () => {
        await onBulkDelete(selectedIds);
        setModalState({ type: null, isOpen: false });
        setSelectedIds([]);
    };
    
    const handleConfirmSingleDelete = () => {
        if (deletingEntry) {
            onDeleteTimeEntry(deletingEntry.id);
            setDeletingEntry(null);
        }
    };

    return (
        <>
            <ConfirmationModal
                isOpen={!!deletingEntry}
                onClose={() => setDeletingEntry(null)}
                onConfirm={handleConfirmSingleDelete}
                title="Confirmar Eliminación"
                message={`¿Estás seguro de que quieres eliminar el registro de la tarea "${deletingEntry?.task}"? Esta acción no se puede deshacer.`}
                confirmText="Eliminar"
            />
             <ConfirmationModal
                isOpen={modalState.isOpen && modalState.type === 'category'}
                onClose={() => setModalState({ type: null, isOpen: false })}
                onConfirm={handleConfirmBulkAction}
                title="Confirmar Acción en Bloque"
                message={`Estás a punto de cambiar la categoría de ${selectedIds.length} registros. ¿Deseas continuar?`}
                confirmText="Confirmar"
            />
            <ReauthDeleteModal
                isOpen={modalState.isOpen && modalState.type === 'delete'}
                onClose={() => setModalState({ type: null, isOpen: false })}
                onConfirm={handleConfirmBulkDelete}
                user={user}
                itemCount={selectedIds.length}
            />

            <div className="space-y-8">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                    <div className="lg:col-span-3 bg-light-surface dark:bg-surface p-6 rounded-lg border border-light-border dark:border-border shadow-md">
                        <h3 className="font-bold text-lg text-light-text-primary dark:text-text-primary mb-6 text-center">Herramientas de Cronometraje</h3>
                        <div className="flex flex-col sm:flex-row gap-8 justify-around items-start">
                            
                            {/* Stopwatch */}
                            <div className="flex flex-col items-center gap-4">
                                <TimerDisplay seconds={stopwatchSeconds} label="Cronómetro" progressColor="text-primary" />
                                <div className="flex gap-3">
                                    <button 
                                        onClick={() => setIsStopwatchRunning(!isStopwatchRunning)} 
                                        className={`p-3 rounded-full transition-colors ${isStopwatchRunning ? 'bg-amber-600 hover:bg-amber-500' : 'bg-secondary hover:bg-green-600'}`}
                                        aria-label={isStopwatchRunning ? 'Pausar cronómetro' : 'Iniciar cronómetro'}
                                    >
                                        {isStopwatchRunning ? <PauseIcon className="w-6 h-6 text-white"/> : <PlayIcon className="w-6 h-6 text-white"/>}
                                    </button>
                                    <button 
                                        onClick={handleStopwatchStopAndTransfer} 
                                        className="p-3 rounded-full bg-red-600 hover:bg-red-500 transition-colors disabled:bg-gray-500 dark:disabled:bg-gray-700 disabled:cursor-not-allowed"
                                        aria-label="Detener y transferir tiempo"
                                        disabled={!isStopwatchRunning && stopwatchSeconds === 0}
                                    >
                                        <StopIcon className="w-6 h-6 text-white"/>
                                    </button>
                                </div>
                            </div>
                             
                            {/* Timer */}
                            <div className="flex flex-col items-center gap-4">
                                <div>
                                    <TimerDisplay seconds={timerSeconds} maxSeconds={initialTimerSeconds} label="Temporizador" progressColor="text-secondary" />
                                </div>
                                <div className="flex gap-3">
                                    <button 
                                        onClick={() => setIsTimerRunning(!isTimerRunning)} 
                                        className={`p-3 rounded-full transition-colors ${isTimerRunning ? 'bg-amber-600 hover:bg-amber-500' : 'bg-primary hover:bg-blue-600'}`}
                                        aria-label={isTimerRunning ? 'Pausar temporizador' : 'Iniciar temporizador'}
                                    >
                                        {isTimerRunning ? <PauseIcon className="w-6 h-6 text-white"/> : <PlayIcon className="w-6 h-6 text-white"/>}
                                    </button>
                                    <button 
                                        onClick={handleTimerStopAndTransfer} 
                                        className="p-3 rounded-full bg-red-600 hover:bg-red-500 transition-colors disabled:bg-gray-500 dark:disabled:bg-gray-700 disabled:cursor-not-allowed"
                                        aria-label="Detener y transferir tiempo"
                                        disabled={initialTimerSeconds - timerSeconds <= 0 && !isTimerRunning}
                                    >
                                        <StopIcon className="w-6 h-6 text-white"/>
                                    </button>
                                    <button 
                                        onClick={handleTimerReset} 
                                        className="p-3 rounded-full bg-gray-500 dark:bg-gray-600 hover:bg-gray-600 dark:hover:bg-gray-500 transition-colors"
                                        aria-label="Reiniciar temporizador"
                                    >
                                        <ReloadIcon className="w-6 h-6 text-white"/>
                                    </button>
                                </div>
                                <div className="flex items-center gap-2 mt-2">
                                    <input type="number" value={timerInputMinutes} onChange={e => setTimerInputMinutes(e.target.value)} className="w-20 bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-2 py-1 text-center"/>
                                    <button onClick={handleSetTimer} className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md">Fijar</button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-2 bg-light-surface dark:bg-surface p-6 rounded-lg border border-light-border dark:border-border shadow-md">
                        <h3 className="font-bold text-lg text-light-text-primary dark:text-text-primary mb-4">{editingEntryId ? 'Editar Registro' : 'Registrar Horas'}</h3>
                        <form onSubmit={handleSaveEntry} className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="date" className="text-sm font-medium mb-1 text-light-text-secondary dark:text-text-secondary block">Fecha</label>
                                    <input type="date" name="date" value={newEntry.date} onChange={handleInputChange} className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" />
                                </div>
                                <div>
                                    <label htmlFor="category" className="text-sm font-medium mb-1 text-light-text-secondary dark:text-text-secondary block">Categoría</label>
                                    <select name="category" value={newEntry.category} onChange={handleInputChange} className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" required>
                                        <option value="" disabled>Selecciona una categoría</option>
                                        {categories.filter(c => c !== "Desconocida").map(c => <option key={c}>{c}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label htmlFor="task" className="text-sm font-medium mb-1 text-light-text-secondary dark:text-text-secondary block">Tarea</label>
                                <input type="text" name="task" value={newEntry.task} onChange={handleInputChange} placeholder="Descripción de la tarea" className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" required />
                            </div>
                            <div>
                                <label htmlFor="hours" className="text-sm font-medium mb-1 text-light-text-secondary dark:text-text-secondary block">Horas (Formato: HH:MM:SS o decimal)</label>
                                <input type="text" name="hours" value={newEntry.hours} onChange={handleInputChange} className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-primary" required />
                            </div>

                            {editingEntryId ? (
                                <div className="flex justify-end items-center gap-4 pt-4">
                                    <button type="button" onClick={resetForm} className="px-4 py-2.5 rounded-md bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-light-text-primary dark:text-text-primary transition-colors">
                                        Cancelar
                                    </button>
                                    <button type="submit" className="flex-1 flex justify-center items-center px-4 py-2.5 bg-secondary text-white rounded-lg hover:bg-green-600 transition-colors duration-200 font-medium shadow-sm">
                                        <EditIcon className="w-5 h-5 mr-2" /> Guardar Cambios
                                    </button>
                                </div>
                            ) : (
                                <button type="submit" className="w-full flex justify-center items-center px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-blue-600 transition-colors duration-200 font-medium shadow-sm">
                                    <AddIcon className="w-5 h-5 mr-2" /> Añadir Registro
                                </button>
                            )}
                        </form>
                    </div>
                </div>
                
                <div className="bg-light-surface dark:bg-surface p-6 rounded-lg border border-light-border dark:border-border shadow-md">
                    <h3 className="font-bold text-lg text-light-text-primary dark:text-text-primary mb-4">Registros de Tiempo</h3>
                     {selectedIds.length > 0 && (
                        <div className="bg-primary/10 dark:bg-primary/20 p-4 rounded-lg border border-primary/30 mb-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                            <div className="flex-shrink-0">
                                <strong className="text-primary font-bold">{selectedIds.length}</strong>
                                <span className="text-light-text-primary dark:text-text-primary ml-2">seleccionados</span>
                            </div>
                            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
                                <div className="flex items-center gap-2">
                                    <select 
                                        value={bulkCategory}
                                        onChange={e => setBulkCategory(e.target.value)}
                                        className="bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                    >
                                        <option value="" disabled>Cambiar categoría...</option>
                                        {categories.filter(c => c !== "Desconocida").sort().map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                    </select>
                                    <button onClick={() => setModalState({ type: 'category', isOpen: true })} disabled={!bulkCategory} className="px-3 py-1.5 text-xs font-semibold bg-primary text-white rounded-md hover:bg-blue-600 disabled:bg-gray-500">Aplicar</button>
                                </div>
                                <button onClick={() => setModalState({ type: 'delete', isOpen: true })} className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold bg-red-600 text-white rounded-md hover:bg-red-700">
                                    <DeleteIcon className="w-4 h-4" /> Eliminar
                                </button>
                            </div>
                        </div>
                    )}
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left text-light-text-secondary dark:text-gray-400">
                            <thead className="text-xs text-light-text-secondary dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700">
                                <tr>
                                     <th scope="col" className="px-4 py-3 w-12">
                                        <input type="checkbox" onChange={handleSelectAll} checked={timeEntries.length > 0 && selectedIds.length === timeEntries.length} className="custom-checkbox h-4 w-4 rounded border-gray-400 bg-white dark:bg-gray-700 text-primary focus:ring-primary cursor-pointer"/>
                                    </th>
                                    <th className="px-6 py-3">Fecha</th>
                                    <th className="px-6 py-3">Categoría</th>
                                    <th className="px-6 py-3">Tarea</th>
                                    <th className="px-6 py-3 text-right">Horas</th>
                                    <th className="px-6 py-3 text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {timeEntries.map(entry => (
                                    <tr key={entry.id} className={`border-b border-light-border dark:border-border hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${selectedIds.includes(entry.id) ? 'bg-primary/10' : 'bg-light-surface dark:bg-surface'}`}>
                                        <td className="px-4 py-4">
                                            <input type="checkbox" checked={selectedIds.includes(entry.id)} onChange={() => handleSelect(entry.id)} className="custom-checkbox h-4 w-4 rounded border-gray-400 bg-white dark:bg-gray-700 text-primary focus:ring-primary cursor-pointer"/>
                                        </td>
                                        <td className="px-6 py-4">{entry.date}</td>
                                        <td className="px-6 py-4">{entry.category}</td>
                                        <td className="px-6 py-4">{entry.task}</td>
                                        <td className="px-6 py-4 text-right font-mono font-medium">{formatTime(entry.hours * 3600)}</td>
                                        <td className="px-6 py-4 text-right">
                                            <button onClick={() => handleEditEntry(entry)} className="p-2 text-light-text-secondary dark:text-text-secondary hover:text-primary transition-colors" aria-label="Editar registro" title="Editar">
                                                <EditIcon className="w-5 h-5"/>
                                            </button>
                                            <button onClick={() => setDeletingEntry(entry)} className="p-2 text-light-text-secondary dark:text-text-secondary hover:text-red-500 transition-colors" aria-label="Eliminar registro" title="Eliminar">
                                                <DeleteIcon className="w-5 h-5"/>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                         {timeEntries.length === 0 && (
                            <p className="text-center py-8 text-light-text-secondary dark:text-text-secondary">
                                No hay registros de tiempo. ¡Añade uno para empezar!
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default TimeLogView;